// src/utils/barCharts.js
// Функции для рендеринга столбчатых диаграмм (оценки, студенты, предметы)

import {
  createFormatters,
  easeOutC,
  niceStep,
  buildPalette,
  positionTooltip,
  createSVGElement,
  createSVGGroup,
  createSVGLine,
  createSVGText,
} from "./chartUtils";

const { fmtNum } = createFormatters();

// Диаграмма оценок по курсам
export function makeMarksChart(cardEl, courseData, chartRegistry, root) {
  chartRegistry.set(cardEl.id, {
    type: "marks",
    data: JSON.parse(JSON.stringify(courseData)),
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");
  let legend = cardEl.querySelector(".legend");
  if (!legend) {
    legend = document.createElement("div");
    legend.className = "legend";
    cardEl.appendChild(legend);
  }

  const WIDTH = 960,
    HEIGHT = 460;
  const M = { top: 18, right: 24, bottom: 92, left: 64 };
  const innerW = WIDTH - M.left - M.right;
  const innerH = HEIGHT - M.top - M.bottom;

  svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const g = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
  const gridG = createSVGGroup(g, null, null, root);
  const barsG = createSVGGroup(g, null, null, root);
  const axisXG = createSVGGroup(g, null, null, root);
  const axisYG = createSVGGroup(g, null, null, root);

  const bars = [];
  let maxMark = 100;
  const yStep = niceStep(maxMark, 6);
  const yMax = Math.ceil(maxMark / yStep) * yStep || 1;

  const yScale = (val) => Math.round(innerH * (1 - val / yMax));

  for (let y = 0; y <= yMax + 1e-9; y += yStep) {
    const yy = yScale(y);
    createSVGLine(gridG, 0, yy, innerW, yy, "grid-line", root);
    const tick = createSVGGroup(axisYG, null, "tick", root);
    createSVGText(tick, fmtNum(y), -10, yy + 4, { "text-anchor": "end" }, root);
  }

  const { colors, hovers } = buildPalette(courseData.length);

  const bandStep = innerW / Math.max(1, courseData.length);
  const bandPad = 0.5;
  const barW = Math.min(
    Math.max(3, bandStep * (1 - bandPad)),
    Math.max(3, bandStep - 8),
    90
  );

  courseData.forEach((course, i) => {
    const cx = (i + 0.5) * bandStep;
    const x = cx - barW / 2;
    const avgMark = course.avgMark || 0;
    const y = yScale(avgMark);
    const h = innerH - y;

    bars.push({
      x,
      y,
      h,
      bw: barW,
      v: avgMark,
      courseName: course.courseName,
      marks: course.marks,
      color: colors[i],
      hoverColor: hovers[i],
      cx,
      idx: i,
    });

    const tick = createSVGGroup(axisXG, null, "tick", root);
    const yy = innerH + 8;
    createSVGText(tick, course.courseName, cx, yy, {
      "text-anchor": "middle",
      "font-size": "11px",
    }, root);
  });

  createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

  const tStart = performance.now(),
    duration = 900;
  function frame(now) {
    const k = Math.min(1, (now - tStart) / duration);
    const t = easeOutC(k);
    barsG.innerHTML = "";

    bars.forEach((b) => {
      const groupEl = createSVGGroup(barsG, null, "bar-group", root);

      const hNow = Math.max(0.5, b.h * t);
      const yNow = innerH - hNow;

      const rect = createSVGElement("rect");
      rect.setAttribute("x", b.x);
      rect.setAttribute("y", yNow);
      rect.setAttribute("width", b.bw);
      rect.setAttribute("height", hNow);
      rect.setAttribute("fill", b.color);
      rect.setAttribute("class", "bar");

      const lbl = createSVGElement("text");
      const baseY = Math.max(12, yNow - 6);
      lbl.setAttribute("x", b.cx);
      lbl.setAttribute("y", baseY);
      lbl.setAttribute("class", "bar-label");
      lbl.setAttribute("text-anchor", "middle");
      lbl.setAttribute("dominant-baseline", "middle");
      lbl.setAttribute("fill", "#2b3242");
      lbl.textContent = fmtNum(b.v);

      const hit = createSVGElement("rect");
      hit.setAttribute("x", b.x - 3);
      hit.setAttribute("y", yNow - 8);
      hit.setAttribute("width", b.bw + 6);
      hit.setAttribute("height", hNow + 12);
      hit.setAttribute("fill", "rgba(0,0,0,0)");

      groupEl.appendChild(rect);
      groupEl.appendChild(lbl);
      groupEl.appendChild(hit);

      const onEnter = (evt) => {
        rect.setAttribute("fill", b.hoverColor);
        lbl.classList.add("hover");
        const marksStr =
          b.marks.length > 0 ? `Baholar: ${b.marks.join(", ")}` : "Baholarsiz";
        tooltip.innerHTML = `<strong>${b.courseName}</strong><br>O'rtacha: ${fmtNum(
          b.v
        )}<br>${marksStr}`;
        tooltip.classList.add("show");
        tooltip.setAttribute("aria-hidden", "false");
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      };
      const onMove = (evt) =>
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      const onLeave = () => {
        rect.setAttribute("fill", b.color);
        lbl.classList.remove("hover");
        tooltip.classList.remove("show");
        tooltip.setAttribute("aria-hidden", "true");
      };

      hit.addEventListener("pointerenter", onEnter);
      hit.addEventListener("pointermove", onMove);
      hit.addEventListener("pointerleave", onLeave);
      hit.addEventListener("touchstart", onEnter, { passive: true });
      hit.addEventListener("touchmove", onMove, { passive: true });
      hit.addEventListener("touchend", onLeave, { passive: true });
    });

    if (k < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  legend.innerHTML = "";

  const allMarks = courseData.flatMap((c) => c.marks);
  const overallAvg =
    allMarks.length > 0
      ? allMarks.reduce((sum, m) => sum + m, 0) / allMarks.length
      : 0;

  const legendItem = document.createElement("div");
  legendItem.className = "legend-item";
  legendItem.style.cssText = `
    background: var(--panel) !important;
    border: 2px solid var(--primary) !important;
    padding: 24px 32px !important;
    border-radius: 12px !important;
    box-shadow: 0 8px 24px rgba(58, 108, 246, 0.15) !important;
    position: relative;
    overflow: hidden;
    animation: legendItemPulse 2s ease-in-out infinite;
  `;
  
  const sw = document.createElement("div");
  sw.className = "swatch";
  sw.style.cssText = `
    background: var(--primary) !important;
    width: 24px !important;
    height: 24px !important;
    border-radius: 6px !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  `;
  
  const title = document.createElement("div");
  title.className = "legend-title";
  title.style.cssText = `
    color: var(--text) !important;
    font-size: 16px !important;
    font-weight: 700 !important;
    letter-spacing: 0.3px !important;
    padding-left: 16px !important;
  `;
  title.textContent = "Umumiy o'rtacha baho";
  
  const val = document.createElement("div");
  val.className = "legend-val";
  val.style.cssText = `
    color: var(--primary) !important;
    font-size: 56px !important;
    font-weight: 800 !important;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
    animation: numberCountUp 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    text-shadow: 0 2px 4px rgba(58, 108, 246, 0.2);
  `;
  
  // Анимация счета от 0 до значения
  let currentVal = 0;
  const animationDuration = 1200;
  const animationStart = performance.now();
  
  function animateValue(now) {
    const elapsed = now - animationStart;
    const progress = Math.min(elapsed / animationDuration, 1);
    currentVal = overallAvg * progress;
    val.textContent = fmtNum(currentVal);
    if (progress < 1) {
      requestAnimationFrame(animateValue);
    }
  }
  
  val.textContent = fmtNum(0);
  requestAnimationFrame(animateValue);
  
  legendItem.appendChild(sw);
  legendItem.appendChild(title);
  legendItem.appendChild(val);
  legend.appendChild(legendItem);
  
  // Добавляем стили анимации в документ если их еще нет
  if (!document.getElementById('legend-animations')) {
    const style = document.createElement('style');
    style.id = 'legend-animations';
    style.textContent = `
      @keyframes legendItemPulse {
        0%, 100% {
          transform: translateY(0px);
          box-shadow: 0 8px 24px rgba(58, 108, 246, 0.25);
        }
        50% {
          transform: translateY(-6px);
          box-shadow: 0 12px 32px rgba(58, 108, 246, 0.35);
        }
      }
      
      @keyframes numberCountUp {
        0% {
          opacity: 0;
          transform: scale(0.8) translateY(10px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Диаграмма одного курса
export function makeSingleCourseChart(
  cardEl,
  courseId,
  courseName,
  allTests,
  passedMarks,
  totalTests,
  chartRegistry,
  root
) {
  chartRegistry.set(cardEl.id, {
    type: "singleCourse",
    data: {
      courseId,
      courseName,
      allTests,
      passedMarks,
      totalTests,
    },
  });

  const chart = cardEl.querySelector(".chart");
  const svg = chart.querySelector("svg");
  const tooltip = chart.querySelector(".tooltip");

  const WIDTH = 960,
    HEIGHT = 460;
  const M = { top: 18, right: 24, bottom: 92, left: 64 };
  const innerW = WIDTH - M.left - M.right;
  const innerH = HEIGHT - M.top - M.bottom;

  svg.setAttribute("viewBox", `0 0 ${WIDTH} ${HEIGHT}`);
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  while (svg.firstChild) svg.removeChild(svg.firstChild);

  const g = createSVGGroup(svg, `translate(${M.left},${M.top})`, null, root);
  const gridG = createSVGGroup(g, null, null, root);
  const barsG = createSVGGroup(g, null, null, root);
  const axisXG = createSVGGroup(g, null, null, root);
  const axisYG = createSVGGroup(g, null, null, root);

  if (!allTests || allTests.length === 0) {
    createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);
    return;
  }

  const bars = [];
  let maxMark = 100;
  const yStep = niceStep(maxMark, 6);
  const yMax = Math.ceil(maxMark / yStep) * yStep || 1;

  const yScale = (val) => Math.round(innerH * (1 - val / yMax));

  for (let y = 0; y <= yMax + 1e-9; y += yStep) {
    const yy = yScale(y);
    createSVGLine(gridG, 0, yy, innerW, yy, "grid-line", root);
    const tick = createSVGGroup(axisYG, null, "tick", root);
    createSVGText(tick, fmtNum(y), -10, yy + 4, { "text-anchor": "end" }, root);
  }

  const { colors, hovers } = buildPalette(allTests.length);

  const bandStep = innerW / Math.max(1, allTests.length);
  const bandPad = 0.5;
  const barW = Math.min(
    Math.max(3, bandStep * (1 - bandPad)),
    Math.max(3, bandStep - 8),
    90
  );

  const shortLabel = (name) => name ? name.replace(/^Nazorat\s+/i, "N") : name;

  allTests.forEach((test, i) => {
    const cx = (i + 0.5) * bandStep;
    const x = cx - barW / 2;

    let testMark = 0;
    let marksInfo = "";

    if (passedMarks.has(test.id)) {
      const markData = passedMarks.get(test.id);
      if (typeof markData === "object" && markData.ball !== undefined) {
        testMark = markData.ball;
        marksInfo = ` (${markData.attempts} ta urinish)`;
      } else {
        testMark = markData;
      }
    }

    const y = yScale(testMark);
    const h = innerH - y;
    const fullName = test.name || `Nazorat ${i + 1}`;

    bars.push({
      x,
      y,
      h,
      bw: barW,
      v: testMark,
      testName: fullName,
      testId: test.id,
      marksInfo: marksInfo,
      color: colors[i],
      hoverColor: hovers[i],
      cx,
      idx: i,
    });

    const tick = createSVGGroup(axisXG, null, "tick", root);
    const yy = innerH + 8;
    createSVGText(
      tick,
      shortLabel(fullName),
      cx,
      yy,
      { "text-anchor": "middle", "font-size": "11px" },
      root
    );
  });

  createSVGLine(axisXG, 0, innerH, innerW, innerH, "axis-line", root);

  const tStart = performance.now(),
    duration = 900;
  function frame(now) {
    const k = Math.min(1, (now - tStart) / duration);
    const t = easeOutC(k);
    barsG.innerHTML = "";

    bars.forEach((b) => {
      const groupEl = createSVGGroup(barsG, null, "bar-group", root);

      const hNow = Math.max(0.5, b.h * t);
      const yNow = innerH - hNow;

      const rect = createSVGElement("rect");
      rect.setAttribute("x", b.x);
      rect.setAttribute("y", yNow);
      rect.setAttribute("width", b.bw);
      rect.setAttribute("height", hNow);
      rect.setAttribute("fill", b.color);
      rect.setAttribute("class", "bar");

      const lbl = createSVGElement("text");
      const baseY = Math.max(12, yNow - 6);
      lbl.setAttribute("x", b.cx);
      lbl.setAttribute("y", baseY);
      lbl.setAttribute("class", "bar-label");
      lbl.setAttribute("text-anchor", "middle");
      lbl.setAttribute("dominant-baseline", "middle");
      lbl.setAttribute("fill", "#2b3242");
      lbl.textContent = fmtNum(b.v);

      const hit = createSVGElement("rect");
      hit.setAttribute("x", b.x - 3);
      hit.setAttribute("y", yNow - 8);
      hit.setAttribute("width", b.bw + 6);
      hit.setAttribute("height", hNow + 12);
      hit.setAttribute("fill", "rgba(0,0,0,0)");

      groupEl.appendChild(rect);
      groupEl.appendChild(lbl);
      groupEl.appendChild(hit);

      const onEnter = (evt) => {
        rect.setAttribute("fill", b.hoverColor);
        lbl.classList.add("hover");
        tooltip.innerHTML = `<strong>${b.testName}</strong><br>Baho: ${fmtNum(
          b.v
        )}${b.marksInfo}`;
        tooltip.classList.add("show");
        tooltip.setAttribute("aria-hidden", "false");
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      };
      const onMove = (evt) =>
        positionTooltip(tooltip, chart, evt.clientX, evt.clientY, "right", 12);
      const onLeave = () => {
        rect.setAttribute("fill", b.color);
        lbl.classList.remove("hover");
        tooltip.classList.remove("show");
        tooltip.setAttribute("aria-hidden", "true");
      };

      hit.addEventListener("pointerenter", onEnter);
      hit.addEventListener("pointermove", onMove);
      hit.addEventListener("pointerleave", onLeave);
      hit.addEventListener("touchstart", onEnter, { passive: true });
      hit.addEventListener("touchmove", onMove, { passive: true });
      hit.addEventListener("touchend", onLeave, { passive: true });
    });

    if (k < 1) requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
